import React, { memo, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Paper, Stack } from '@mui/material'
import { selectActiveChartId, selectChartConfigs, selectDrawings } from '../../../selectors/chart'
import { AppContext } from '../../../App'
import { ChartDrawingItem, DrawingSettingItem, SettingValues } from '../drawingTools/drawing.interface'
import ChartMiniSettingsMenuItem from './chartMiniSettingsMenuItem'
import useStyles from './styles'
import { ExtraSmallButton } from '../../custom'
import SettingsOutlinedIcon from '../../../assets/icons/SettingOutlined'
import { selectLayoutWidgets } from '../../../selectors/widgetDashboard'
import useSelectActiveLayout from '../../../hooks/useSelectActiveLayout'
import SyncDrawingIcon from '../../../assets/icons/drawingTools/syncDrawing'
import DeleteIcon from '../../../assets/icons/DeleteIcon'
import { callNative, isNativeEmbedded } from '../../../util/nativeBridge'
import DrawingAlertButton from './DrawingAlertButton'
import { isAlertableDrawingType } from '../../../util/alerts'


export type ChartDrawingMiniSettingsProps = {
    id: string,
    openFullSettings: () => void
}
const ChartDrawingMiniSettings: React.FC<ChartDrawingMiniSettingsProps> = ({ id, openFullSettings }) => {
    const { classes } = useStyles()
    const activeChartId = useSelector(selectActiveChartId)
    const [activeLayoutId, activeLayout] = useSelectActiveLayout()
    const appContext = useContext(AppContext)
    const widgets = useSelector(selectLayoutWidgets(activeLayoutId))
    const chartConfigs = useSelector(selectChartConfigs)
    const settingsGroups = id ? appContext.getChart(activeChartId).getDrawingSettings(id) : []
    const miniSettings = id ? appContext.getChart(activeChartId).getDrawingMiniSettings(id) : []
    const drawingConfig = appContext.getChart(activeChartId).getDrawingConfig(id)
    const drawingType = (drawingConfig as ChartDrawingItem)?.type ?? (drawingConfig as ChartDrawingItem)?.options?.drawingType ?? 'trendLine'
    const showDrawingAlert = isAlertableDrawingType(drawingType)
    const allDrawings = useSelector(selectDrawings)
    const [settingsValues, setSettingsValues] = useState<any>()
    const settings = useMemo(() => {
        let items: DrawingSettingItem<any, SettingValues>[] = []
        settingsGroups.reduce((acc, sg) => {
            sg.settings.forEach(s => {
                if (miniSettings.indexOf(s.name.toString()) !== -1) {
                    items = [...items, s]
                }
            })
            return acc
        }, items)
        return items
    }, [id, settingsGroups, settingsValues, miniSettings])

    const linked = useMemo(() => {
        let linkedDrawing = !!(drawingConfig as ChartDrawingItem)?.referenceDrawingId
        for (let wg of Object.values(widgets)) {
            if (linkedDrawing) break
            if (wg.settings.chartId) {
                const drawings = allDrawings[wg.settings.chartId]
                if (!drawings || !drawings.length) return
                for (let dr of drawings) {
                    if (dr.referenceDrawingId === id) {
                        linkedDrawing = true
                        break
                    }
                }
            }
        }
        return linkedDrawing
    }, [id, drawingConfig, widgets, chartConfigs, allDrawings])

    useEffect(() => {
        if (!id) return
        setSettingsValues(appContext.getChart(activeChartId).getSettingsValues(id))
    }, [id])


    const chart = appContext.getChart(activeChartId)
    const priceScaleWidth = chart.chartApi.priceScale(chart.defaultPriceScaleId).width()

    const onValueChange = useCallback((name: string, value: SettingValues) => {
        appContext.getChart(activeChartId).updateDrawingSettings(id, {
            [name]: value
        })
        setSettingsValues(appContext.getChart(activeChartId).getSettingsValues(id))
    }, [id, activeChartId])

    const settingsItems: ReactNode[] = useMemo(() => {
        if (settingsValues) {
            return settings.map((s) => <ChartMiniSettingsMenuItem key={`${id}-${s.name.toString()}`} id={id}
                                                                  onValueChange={onValueChange} setting={s}
                                                                  value={settingsValues[s.name.toString()]} />)
        } else {
            return []
        }
    }, [settings, settingsValues, id, onValueChange])

    const onDeleteClick = () => {
        const chart = appContext.getChart(activeChartId)
        if (chart) {
            chart.removeDrawing(id)
        }
    }
    const showSyncButton: boolean = useMemo(() => {
        return !(!activeLayout || activeLayout.singleMode || !Object.values(widgets))

    }, [activeLayoutId, activeLayout])

    const onSyncClick = () => {
        const chart = appContext.getChart(activeChartId)
        if (linked) {
            chart.unsyncDrawing(id)
        } else {
            chart.syncDrawing(id)
        }
    }


    return (
        <Paper
            elevation={24}
            className={classes.miniSettingsContainer}
            sx={{ right: `${priceScaleWidth + 10}px`, display: 'flex', alignItems: 'center', boxShadow: 24 }}
        >
            <Stack direction={'row'}
                   spacing={0.5}
                   alignItems={'center'}
                   justifyContent={'center'}
                   width={'fitContent'}>
                {settingsItems}
                {showDrawingAlert && (
                    <DrawingAlertButton drawingId={id} chartId={activeChartId} drawingType={drawingType} />
                )}
                <ExtraSmallButton onClick={() => {
                    if (isNativeEmbedded()) {
                        const chart = appContext.getChart(activeChartId)
                        const settingsGroups = chart.getDrawingSettings(id)
                        const currentValues = chart.getSettingsValues(id)
                        const settingsWithValues = settingsGroups.map(group => ({
                            ...group,
                            settings: group.settings.map(setting => ({
                                ...setting,
                                defaultValue: currentValues[setting.name] !== undefined
                                    ? currentValues[setting.name]
                                    : setting.defaultValue
                            }))
                        }))
                        callNative('onDrawingSettingsClicked', '', id, JSON.stringify(settingsWithValues))
                    } else {
                        openFullSettings()
                    }
                }} color="inherit">
                    <SettingsOutlinedIcon fontSize='small' />
                </ExtraSmallButton>
                {settingsValues && !((settingsValues['indicator']) ?? false) &&
                    <ExtraSmallButton onClick={onDeleteClick} color="inherit">
                        <DeleteIcon fontSize='small' />
                    </ExtraSmallButton>}
                {
                    showSyncButton &&
                    <ExtraSmallButton onClick={onSyncClick} color={linked ? 'primary' : 'inherit'}>
                        <SyncDrawingIcon />
                    </ExtraSmallButton>
                }
            </Stack>
        </Paper>
    )
}

export default memo(ChartDrawingMiniSettings)
